import { useAuth } from "../../context/auth-context";
import type { AdminSpreadSwapStatus } from "./adminSpreadSummaries";
import type {
  AdminSpreadTransactionsDataEnvelope,
  AdminSpreadTransactionsDataPayload,
} from "./adminTransactionsData";
import { apiQueryKey } from "./queryKeys";
import { useApiQuery } from "./useApiQuery";

const DEFAULT_LIMIT = 25;

export type UseAdminSpreadTransactionsDataParams = {
  page: number;
  /** Exact match on tx id, asset (e.g. BTC), Obiex ref, ledger id, or user id. */
  search?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  /** TRX swap status. Omit for all. */
  status?: AdminSpreadSwapStatus;
  enabled?: boolean;
};

/**
 * Paginated spread (fee) rows from GET api/admin/transactions/data
 * (?type=spread[&status=PENDING|PROCESSING|SWAPPED|FAILED]).
 */
export function useAdminSpreadTransactionsDataQuery(
  params: UseAdminSpreadTransactionsDataParams
) {
  const { isAuthenticated } = useAuth();
  const {
    page,
    search,
    startDate,
    endDate,
    limit = DEFAULT_LIMIT,
    status,
    enabled: enabledParam = true,
  } = params;
  const trimmedSearch = search?.trim() || undefined;

  return useApiQuery<
    AdminSpreadTransactionsDataEnvelope,
    AdminSpreadTransactionsDataPayload
  >({
    queryKey: apiQueryKey("admin-transactions-data", {
      type: "spread",
      page,
      limit,
      search: trimmedSearch ?? null,
      startDate: startDate ?? null,
      endDate: endDate ?? null,
      status: status ?? null,
    }),
    url: "api/admin/transactions/data",
    params: {
      type: "spread",
      page,
      limit,
      search: trimmedSearch,
      startDate,
      endDate,
      status,
    },
    enabled: Boolean(isAuthenticated) && enabledParam,
    select: (body) => body.data,
  });
}
